// frontend/src/apps/los/components/DynamicFormBuilder/DynamicFormBuilder.tsx
/**
 * DynamicFormBuilder - A Frappe-like generic form builder component
 * 
 * Allows creating forms dynamically with configurable fields at runtime.
 * Each field can be configured with:
 * - Field type (text, number, email, select, checkbox, etc.)
 * - Label (display name)
 * - Backend field name (API key)
 * - Validation rules
 * - Options (for select/radio)
 * - Default value
 * 
 * Usage in Journey Builder:
 * 1. Add this component to a page
 * 2. Configure fields in the builder
 * 3. Fields are automatically saved to backend with configured field names
 */

import React, { useState, useEffect } from 'react';
import { z } from 'zod';

// Field type definitions
export type FieldType = 
  | 'text' 
  | 'number' 
  | 'email' 
  | 'tel' 
  | 'date' 
  | 'select' 
  | 'radio' 
  | 'checkbox' 
  | 'textarea'
  | 'file';

export interface FieldConfig {
  id: string;
  type: FieldType;
  label: string;
  fieldName: string; // Backend field name
  placeholder?: string;
  required?: boolean;
  options?: Array<{ label: string; value: string }>; // For select/radio
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    minLength?: number;
    maxLength?: number;
  };
  defaultValue?: any;
  helpText?: string;
}

export interface DynamicFormBuilderProps {
  value?: Record<string, any>;
  onChange?: (value: Record<string, any>) => void;
  disabled?: boolean;
  isBuilderMode?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  // Configuration passed from PageConfig.layout
  fields?: FieldConfig[];
}

const DynamicFormBuilder: React.FC<DynamicFormBuilderProps> = ({
  value = {},
  onChange,
  disabled = false,
  isBuilderMode = false,
  registerSubmit,
  fields = [],
}) => {
  const [formData, setFormData] = useState<Record<string, any>>(value || {});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [builderFields, setBuilderFields] = useState<FieldConfig[]>(fields);

  // Sync with external value changes
  useEffect(() => {
    if (value && JSON.stringify(value) !== JSON.stringify(formData)) {
      setFormData(value);
    }
  }, [value]);

  // Register submit handler for JourneyPage
  useEffect(() => {
    if (registerSubmit && !isBuilderMode) {
      registerSubmit(async () => {
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
          setErrors(validationErrors);
          return false;
        }
        return true;
      });
    }
  }, [registerSubmit, isBuilderMode, formData, builderFields]);

  const validateForm = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    builderFields.forEach((field) => {
      const fieldValue = formData[field.fieldName];

      // Required validation
      if (field.required && (!fieldValue || fieldValue === '')) {
        newErrors[field.fieldName] = `${field.label} is required`;
        return;
      }

      if (!fieldValue) return; // Skip other validations if empty and not required

      // Type-specific validation
      if (field.type === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(fieldValue)) {
          newErrors[field.fieldName] = 'Invalid email address';
        }
      }

      if (field.type === 'tel') {
        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(fieldValue)) {
          newErrors[field.fieldName] = 'Invalid phone number (10 digits required)';
        }
      }

      // Custom validation rules
      if (field.validation) {
        const { min, max, minLength, maxLength, pattern } = field.validation;

        if (field.type === 'number') {
          const numValue = Number(fieldValue);
          if (min !== undefined && numValue < min) {
            newErrors[field.fieldName] = `Minimum value is ${min}`;
          }
          if (max !== undefined && numValue > max) {
            newErrors[field.fieldName] = `Maximum value is ${max}`;
          }
        }

        if (typeof fieldValue === 'string') {
          if (minLength && fieldValue.length < minLength) {
            newErrors[field.fieldName] = `Minimum length is ${minLength} characters`;
          }
          if (maxLength && fieldValue.length > maxLength) {
            newErrors[field.fieldName] = `Maximum length is ${maxLength} characters`;
          }
          if (pattern && !new RegExp(pattern).test(fieldValue)) {
            newErrors[field.fieldName] = `Invalid format`;
          }
        }
      }
    });

    return newErrors;
  };

  const handleFieldChange = (fieldName: string, newValue: any) => {
    const updatedData = { ...formData, [fieldName]: newValue };
    setFormData(updatedData);
    if (onChange) {
      onChange(updatedData);
    }
    // Clear error for this field
    if (errors[fieldName]) {
      setErrors({ ...errors, [fieldName]: '' });
    }
  };

  const addField = () => {
    const newField: FieldConfig = {
      id: `field_${Date.now()}`,
      type: 'text',
      label: 'New Field',
      fieldName: `field_${Date.now()}`,
      required: false,
    };
    setBuilderFields([...builderFields, newField]);
  };

  const updateField = (index: number, updates: Partial<FieldConfig>) => {
    const updated = [...builderFields];
    updated[index] = { ...updated[index], ...updates };
    setBuilderFields(updated);
    // Notify parent to save in PageConfig.layout
    if (onChange) {
      onChange({ ...formData, __fields: updated });
    }
  };

  const removeField = (index: number) => {
    const updated = builderFields.filter((_, i) => i !== index);
    setBuilderFields(updated);
    if (onChange) {
      onChange({ ...formData, __fields: updated });
    }
  };

  const renderField = (field: FieldConfig) => {
    const fieldValue = formData[field.fieldName] || field.defaultValue || '';
    const hasError = !!errors[field.fieldName];

    const commonProps = {
      disabled,
      className: `w-full px-3 py-2 border rounded-md ${
        hasError ? 'border-red-500' : 'border-gray-300'
      } focus:outline-none focus:ring-2 focus:ring-intellend-green`,
    };

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            {...commonProps}
            value={fieldValue}
            placeholder={field.placeholder}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
            rows={4}
          />
        );

      case 'select':
        return (
          <select
            {...commonProps}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
          >
            <option value="">Select {field.label}</option>
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.map((opt) => (
              <label key={opt.value} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name={field.fieldName}
                  value={opt.value}
                  checked={fieldValue === opt.value}
                  onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
                  disabled={disabled}
                  className="text-intellend-green focus:ring-intellend-green"
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={!!fieldValue}
              onChange={(e) => handleFieldChange(field.fieldName, e.target.checked)}
              disabled={disabled}
              className="text-intellend-green focus:ring-intellend-green"
            />
            <span>{field.label}</span>
          </label>
        );

      default:
        return (
          <input
            {...commonProps}
            type={field.type}
            value={fieldValue}
            placeholder={field.placeholder}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
          />
        );
    }
  };

  // ── Builder Mode: Field Configuration UI ──────────────────────────────────
  if (isBuilderMode) {
    return (
      <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-700">Dynamic Form Builder</h3>
          <button
            onClick={addField}
            className="px-3 py-1 bg-intellend-green text-white rounded hover:bg-intellend-green/90 text-sm"
          >
            + Add Field
          </button>
        </div>

        {builderFields.length === 0 && (
          <p className="text-gray-500 text-center py-8">
            No fields configured. Click "Add Field" to start building your form.
          </p>
        )}

        <div className="space-y-4">
          {builderFields.map((field, index) => (
            <div key={field.id} className="p-4 bg-white border rounded-lg">
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Field Label
                  </label>
                  <input
                    type="text"
                    value={field.label}
                    onChange={(e) => updateField(index, { label: e.target.value })}
                    className="w-full px-2 py-1 border rounded text-sm"
                    placeholder="e.g., Full Name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Backend Field Name
                  </label>
                  <input
                    type="text"
                    value={field.fieldName}
                    onChange={(e) => updateField(index, { fieldName: e.target.value })}
                    className="w-full px-2 py-1 border rounded text-sm font-mono"
                    placeholder="e.g., full_name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Field Type
                  </label>
                  <select
                    value={field.type}
                    onChange={(e) => updateField(index, { type: e.target.value as FieldType })}
                    className="w-full px-2 py-1 border rounded text-sm"
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="email">Email</option>
                    <option value="tel">Phone</option>
                    <option value="date">Date</option>
                    <option value="select">Select</option>
                    <option value="radio">Radio</option>
                    <option value="checkbox">Checkbox</option>
                    <option value="textarea">Textarea</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Placeholder
                  </label>
                  <input
                    type="text"
                    value={field.placeholder || ''}
                    onChange={(e) => updateField(index, { placeholder: e.target.value })}
                    className="w-full px-2 py-1 border rounded text-sm"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={field.required || false}
                      onChange={(e) => updateField(index, { required: e.target.checked })}
                      className="text-intellend-green"
                    />
                    <span className="text-xs font-medium">Required</span>
                  </label>
                </div>
              </div>

              {(field.type === 'select' || field.type === 'radio') && (
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Options (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={field.options?.map((o) => o.label).join(', ') || ''}
                    onChange={(e) => {
                      const opts = e.target.value.split(',').map((v) => {
                        const trimmed = v.trim();
                        return { label: trimmed, value: trimmed.toLowerCase().replace(/\s+/g, '_') };
                      });
                      updateField(index, { options: opts });
                    }}
                    className="w-full px-2 py-1 border rounded text-sm"
                    placeholder="e.g., Option 1, Option 2, Option 3"
                  />
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={() => removeField(index)}
                  className="px-3 py-1 text-red-600 hover:bg-red-50 rounded text-sm"
                >
                  Remove Field
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Journey Mode: Render Form ──────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {builderFields.map((field) => (
        <div key={field.id}>
          {field.type !== 'checkbox' && (
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
          )}
          {renderField(field)}
          {errors[field.fieldName] && (
            <p className="text-red-500 text-sm mt-1">{errors[field.fieldName]}</p>
          )}
          {field.helpText && !errors[field.fieldName] && (
            <p className="text-gray-500 text-xs mt-1">{field.helpText}</p>
          )}
        </div>
      ))}

      {builderFields.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No fields configured for this form.
        </div>
      )}
    </div>
  );
};

export default DynamicFormBuilder;
